/**
 * Empalmes de vías (paso 1.4): el imán que une un punto a otra vía al dibujar o al editar, y los
 * extremos que quedaron sueltos. El grafo de circulación sigue empalmando a 0.7 m (como el v6); el
 * imán deja el punto exactamente sobre la otra vía, así el empalme no depende de acertar a ojo.
 */

import type { Espacio, Punto, TipoVia, Via } from '../dominio/espacio';
import { EMPALME_M, tramosDe, type Tramo } from './circulacion';
import { distancia, intersectarSegmentos, proyectarEnSegmento } from './geometria';

/** El imán alcanza hasta este margen fuera del borde de la otra calzada. */
export const IMAN_BORDE_M = 1.5;
/** Un extremo junto al borde del espacio continúa como calle (se dibuja fuera del marco). */
export const BORDE_CALLE_M = 3;

export interface Iman {
  /** Punto donde queda el empalme. */
  punto: Punto;
  viaId: string;
  /** `punto`: se une a un punto de la otra vía; `tramo`: a su eje, entre dos puntos. */
  tipo: 'punto' | 'tramo';
}

/** ¿Una vía de este tipo se empalma con la otra? Los autos no circulan por las sendas. */
function seEmpalman(tipo: TipoVia, otra: Via): boolean {
  return tipo === 'peatonal' || otra.tipo !== 'peatonal';
}

/**
 * Imán de vías: si `pt` queda sobre otra vía o a menos de 1.5 m de su borde, devuelve el punto de
 * empalme sobre su eje (o sobre uno de sus puntos, si hay uno cerca). `excluir` son las vías que se
 * están editando.
 */
export function imanDeVias(
  pt: Punto,
  vias: readonly Via[],
  tipo: TipoVia,
  excluir: ReadonlySet<string>,
): Iman | null {
  const candidatos: { iman: Iman; puntaje: number }[] = [];
  for (const v of vias) {
    if (excluir.has(v.id) || !seEmpalman(tipo, v) || v.puntos.length < 2) continue;
    const alcance = v.ancho / 2 + IMAN_BORDE_M;
    // Un punto de la otra vía tiene prioridad sobre su eje si está casi igual de cerca.
    for (const q of v.puntos) {
      const d = distancia(pt, q);
      if (d <= alcance)
        candidatos.push({
          iman: { punto: { x: q.x, z: q.z }, viaId: v.id, tipo: 'punto' },
          puntaje: d - 1,
        });
    }
    for (let i = 0; i < v.puntos.length - 1; i++) {
      const pr = proyectarEnSegmento(pt, v.puntos[i]!, v.puntos[i + 1]!);
      if (pr.d <= alcance) {
        const punto = { x: redondear(pr.q.x), z: redondear(pr.q.z) };
        candidatos.push({ iman: { punto, viaId: v.id, tipo: 'tramo' }, puntaje: pr.d });
      }
    }
  }
  let mejor: { iman: Iman; puntaje: number } | undefined;
  for (const c of candidatos) if (!mejor || c.puntaje < mejor.puntaje) mejor = c;
  return mejor?.iman ?? null;
}

/** Distancia a la que dos puntos de vías distintas son el mismo (una esquina compartida). */
const MISMO_PUNTO_M = 0.05;

/**
 * Puntos de otras vías que coinciden con el punto `indice` de la vía: al moverlo se mueven juntos,
 * así una esquina compartida no se desarma.
 */
export function puntosUnidos(
  vias: readonly Via[],
  viaId: string,
  indice: number,
): { viaId: string; indice: number }[] {
  const via = vias.find((v) => v.id === viaId);
  const p = via?.puntos[indice];
  if (!via || !p) return [];
  const r: { viaId: string; indice: number }[] = [];
  for (const v of vias) {
    if (v.id === viaId || !seEmpalman(via.tipo, v)) continue;
    v.puntos.forEach((q, i) => {
      if (distancia(p, q) < MISMO_PUNTO_M) r.push({ viaId: v.id, indice: i });
    });
  }
  return r;
}

/** Edición de los puntos de una vía. `insertar` deja el punto nuevo en la posición `indice`. */
export type EdicionDePuntos =
  | { tipo: 'mover'; indice: number; punto: Punto }
  | { tipo: 'insertar'; indice: number; punto: Punto }
  | { tipo: 'borrar'; indice: number };

/** Puntos nuevos de una vía cambiada por una edición. */
export interface CambioDeVia {
  viaId: string;
  puntos: Punto[];
}

/**
 * Edita los puntos de una vía y acompaña a lo que está unido a ella (paso 1.4), para que los
 * empalmes no se desarmen:
 *
 * - los extremos de otras vías que terminan sobre uno de sus tramos (empalmes en T, a menos de
 *   0.7 m, como en el grafo) conservan su lugar relativo en el tramo que cambia;
 * - al mover un punto, los puntos de otras vías que coinciden con él (una esquina compartida) se
 *   mueven juntos.
 *
 * Devuelve los puntos nuevos de cada vía que cambia, la editada primero. Una vía no baja de 2
 * puntos ni se inserta fuera de sus tramos: en esos casos no cambia nada.
 */
export function editarPuntosDeVia(
  vias: readonly Via[],
  viaId: string,
  edicion: EdicionDePuntos,
): CambioDeVia[] {
  const via = vias.find((v) => v.id === viaId);
  if (!via) return [];
  const antes = via.puntos;
  const i = edicion.indice;
  let nuevos: Punto[];
  if (edicion.tipo === 'mover') {
    if (!antes[i]) return [];
    nuevos = antes.map((q, k) => (k === i ? copiar(edicion.punto) : q));
  } else if (edicion.tipo === 'insertar') {
    if (i < 1 || i > antes.length - 1) return [];
    nuevos = [...antes.slice(0, i), copiar(edicion.punto), ...antes.slice(i)];
  } else {
    if (antes.length <= 2 || !antes[i]) return [];
    nuevos = antes.filter((_, k) => k !== i);
  }

  const cambios = new Map<string, Punto[]>([[via.id, nuevos]]);
  const puntosDe = (v: Via) => {
    let p = cambios.get(v.id);
    if (!p) {
      p = [...v.puntos];
      cambios.set(v.id, p);
    }
    return p;
  };

  // Empalmes en T: extremos de otras vías sobre un tramo de la vía, con su posición relativa.
  for (const v of vias) {
    if (v.id === via.id || v.puntos.length < 2 || !seEmpalman(v.tipo, via)) continue;
    for (const j of [0, v.puntos.length - 1]) {
      const e = v.puntos[j]!;
      let mejor: { k: number; t: number; d: number } | null = null;
      for (let k = 0; k < antes.length - 1; k++) {
        const pr = proyectarEnSegmento(e, antes[k]!, antes[k + 1]!);
        if (pr.d < EMPALME_M && (!mejor || pr.d < mejor.d)) mejor = { k, t: pr.t, d: pr.d };
      }
      if (!mejor) continue;
      const nuevo = acompanar(antes, nuevos, edicion, mejor.k, mejor.t, e);
      if (nuevo && distancia(nuevo, e) > 1e-9) puntosDe(v)[j] = nuevo;
    }
  }
  // Esquinas compartidas: se mueven junto con el punto.
  if (edicion.tipo === 'mover')
    for (const u of puntosUnidos(vias, via.id, i)) {
      const v = vias.find((x) => x.id === u.viaId)!;
      puntosDe(v)[u.indice] = copiar(edicion.punto);
    }

  return [...cambios].map(([id, puntos]) => ({ viaId: id, puntos }));
}

/**
 * Dónde queda un extremo unido en T al tramo `k` (en la posición relativa `t`) después de la
 * edición; `null` si ese tramo no cambia.
 */
function acompanar(
  antes: readonly Punto[],
  nuevos: readonly Punto[],
  edicion: EdicionDePuntos,
  k: number,
  t: number,
  e: Punto,
): Punto | null {
  const i = edicion.indice;
  const en = (a: Punto, b: Punto, f: number) =>
    redondear3({ x: a.x + (b.x - a.x) * f, z: a.z + (b.z - a.z) * f });
  if (edicion.tipo === 'mover') {
    if (k !== i - 1 && k !== i) return null;
    return en(nuevos[k]!, nuevos[k + 1]!, t);
  }
  if (edicion.tipo === 'insertar') {
    if (k !== i - 1) return null;
    // El punto nuevo parte el tramo donde se proyecta; cada mitad se lleva sus empalmes.
    const s = Math.min(
      0.99,
      Math.max(0.01, proyectarEnSegmento(edicion.punto, antes[k]!, antes[k + 1]!).t),
    );
    return t <= s
      ? en(nuevos[k]!, nuevos[k + 1]!, t / s)
      : en(nuevos[k + 1]!, nuevos[k + 2]!, (t - s) / (1 - s));
  }
  // Borrar: los dos tramos del punto quedan en uno; el extremo va al punto más cercano de la vía.
  if (k !== i - 1 && k !== i) return null;
  let mejor: { q: Punto; d: number } | null = null;
  for (let m = 0; m < nuevos.length - 1; m++) {
    const pr = proyectarEnSegmento(e, nuevos[m]!, nuevos[m + 1]!);
    if (!mejor || pr.d < mejor.d) mejor = { q: pr.q, d: pr.d };
  }
  return mejor ? redondear3(mejor.q) : null;
}

const copiar = (p: Punto): Punto => ({ x: p.x, z: p.z });
const redondear3 = (p: Punto): Punto => ({
  x: Math.round(p.x * 1000) / 1000,
  z: Math.round(p.z * 1000) / 1000,
});

/**
 * Traslada vías completas (por ejemplo, con las flechas del teclado). Como al editar un punto, lo
 * que termina sobre ellas las acompaña: los extremos de otras vías apoyados en sus tramos y los
 * puntos que coinciden con los suyos se desplazan lo mismo. Devuelve los puntos nuevos de cada vía
 * que cambia.
 */
export function trasladarVias(
  vias: readonly Via[],
  ids: ReadonlySet<string>,
  dx: number,
  dz: number,
): CambioDeVia[] {
  const mover = (p: Punto): Punto => redondear3({ x: p.x + dx, z: p.z + dz });
  const movidas = vias.filter((v) => ids.has(v.id));
  const r: CambioDeVia[] = movidas.map((v) => ({ viaId: v.id, puntos: v.puntos.map(mover) }));
  for (const v of vias) {
    if (ids.has(v.id) || v.puntos.length < 2) continue;
    const base = movidas.filter((m) => seEmpalman(v.tipo, m));
    if (base.length === 0) continue;
    const ultimo = v.puntos.length - 1;
    let cambio = false;
    const puntos = v.puntos.map((q, j) => {
      const sobreTramo =
        (j === 0 || j === ultimo) &&
        base.some((m) =>
          m.puntos.some(
            (a, k) => k > 0 && proyectarEnSegmento(q, m.puntos[k - 1]!, a).d < EMPALME_M,
          ),
        );
      const enPunto = base.some((m) => m.puntos.some((a) => distancia(a, q) < MISMO_PUNTO_M));
      if (!sobreTramo && !enPunto) return q;
      cambio = true;
      return mover(q);
    });
    if (cambio) r.push({ viaId: v.id, puntos });
  }
  return r;
}

export interface ExtremoSuelto {
  viaId: string;
  /** Índice del punto: 0 (inicio) o el último. */
  indice: number;
  punto: Punto;
  /** El extremo pisa otra calzada sin estar unido a ella (parece unido y no lo está). */
  sobreOtraVia: boolean;
}

/**
 * Extremos de vías vehiculares que no se unen a nada: ni a otra vía (a 0.7 m de su eje), ni al
 * borde del espacio (la calle sigue afuera), ni a la boca de una estación cercana. Un extremo que
 * pisa otra calzada sin unirse a su eje siempre se avisa: es el error típico de un empalme a ojo.
 */
export function extremosSueltos(
  espacio: Pick<Espacio, 'marco' | 'layout'>,
  anclas: readonly Punto[],
): ExtremoSuelto[] {
  const { marco } = espacio;
  const tramos = tramosDe(espacio.layout.vias, 'vehicular');
  const r: ExtremoSuelto[] = [];
  for (const via of espacio.layout.vias) {
    if (via.tipo === 'peatonal' || via.puntos.length < 2) continue;
    const ultimo = via.puntos.length - 1;
    for (const indice of [0, ultimo]) {
      const e = via.puntos[indice]!;
      // Mismo criterio que el grafo de circulación (empalme en T).
      const unido = tramos.some(
        (s) =>
          !(s.via === via && (s.a === e || s.b === e)) &&
          proyectarEnSegmento(e, s.a, s.b).d < EMPALME_M,
      );
      if (unido) continue;
      // Pisa la calzada de otra vía (entre sus puntos, no en su esquina) con la que no está unida.
      const sobreOtraVia = tramos.some((s) => {
        if (s.via === via || unidas(via, s.via, tramos)) return false;
        const pr = proyectarEnSegmento(e, s.a, s.b);
        return pr.t > 1e-6 && pr.t < 1 - 1e-6 && pr.d <= s.via.ancho / 2;
      });
      const enElBorde =
        Math.abs(e.x - marco.x) >= marco.ancho / 2 - BORDE_CALLE_M ||
        Math.abs(e.z - marco.z) >= marco.fondo / 2 - BORDE_CALLE_M;
      const sirveAUnaBoca = anclas.some((a) => distancia(a, e) <= via.ancho / 2 + 1);
      if (!sobreOtraVia && (enElBorde || sirveAUnaBoca)) continue;
      r.push({ viaId: via.id, indice, punto: e, sobreOtraVia });
    }
  }
  return r;
}

/** ¿Las dos vías ya se unen en algún punto (se cruzan o una termina sobre la otra)? */
function unidas(a: Via, b: Via, tramos: readonly Tramo[]): boolean {
  const de = (v: Via) => tramos.filter((s) => s.via === v);
  const ta = de(a);
  const tb = de(b);
  const terminaSobre = (v: Via, otros: readonly Tramo[]) =>
    [v.puntos[0]!, v.puntos[v.puntos.length - 1]!].some((e) =>
      otros.some((s) => proyectarEnSegmento(e, s.a, s.b).d < EMPALME_M),
    );
  return (
    terminaSobre(a, tb) ||
    terminaSobre(b, ta) ||
    ta.some((s) => tb.some((r) => intersectarSegmentos(s.a, s.b, r.a, r.b) !== null))
  );
}

const redondear = (v: number) => Math.round(v * 100) / 100;
